import React, { ChangeEvent, FC, useMemo, useRef, useState } from 'react';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import {
  filterCustomFormulaMetric,
  filterMetrics,
} from 'src/components/Forms/CustomFormulaForm/Metrics';
import {
  CollapsesContainer,
  MetricsCardTitle,
} from 'src/components/Forms/CustomFormulaForm/Metrics/styled';
import { SearchInput } from 'src/components/Forms/FormInputs/SearchInput';
import { ReactComponent as CustomFormulaIcon } from 'src/icons/customFormula.svg';
import { ReactComponent as FacebookAdsIcon } from 'src/icons/facebook.svg';
import { ReactComponent as GoogleAdsIcon } from 'src/icons/googleAds.svg';
import { ReactComponent as GoogleAnalyticsIcon } from 'src/icons/googleAnalytics.svg';
import { ReactComponent as GoogleSearchConsoleIcon } from 'src/icons/googleSearchConsole.svg';
import { ReactComponent as ShopifyIcon } from 'src/icons/shopify.svg';
import { ReactComponent as MagentoIcon } from 'src/icons/magento.svg';
import kipifyLogo from 'src/icons/kipify-logo.png';
import { groupMetricsByCategory } from 'src/utils';
import {
  ConnectorLabel,
  ConnectorType,
  Metric,
  MetricsByConnectorType,
} from 'src/utils/gql/types';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';

import { MetricsCard } from '../styled';
import { DashboardForm, MetricsProps } from '../types';

import { MetricsDropdown } from './MetricsDropdown';

export const MetricsContent: FC<MetricsProps> = ({
  facebookData,
  googleAdsData,
  googleAnalyticsData,
  googleSearchConsoleData,
  shopifyData,
  magentoData,
  kipifyData,
  customFormulaData,
  expandedCollapseType,
  onToggleCollapse,
  openOrCloseAllMetrics,
}) => {
  const { databaseConnectorId, databaseMetrics } = useConnectors() as any;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const metricsContainerRef = useRef<HTMLDivElement | null>(null);

  const [search, setSearch] = useState('');

  const { setValue, getValues } = useFormContext<DashboardForm>();

  const metrics = getValues('metrics');

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    openOrCloseAllMetrics(!!event.target.value);
    setSearch(event.target.value);
  };

  const clearSearch = () => {
    openOrCloseAllMetrics(false);

    setSearch('');
  };

  const handleClickMetric = (metric: Metric) => {
    const metricsId = metrics.map(({ id }) => id);

    if (metricsId.includes(metric.id)) {
      setValue(
        'metrics',
        metrics.filter(({ id }) => id !== metric.id),
      );
    } else {
      setValue('metrics', [...metrics, metric]);
    }
  };

  // temporary code

  const preFilteredMetrics = useMemo(
    () =>
      [...googleAdsData.metrics].sort((a, b) => a.label.localeCompare(b.label)),
    [googleAdsData.metrics],
  );
  /// /

  const memoizedMetrics = useMemo(() => {
    const filteredFacebookMetrics = filterMetrics(search, facebookData.metrics);
    const filteredGoogleAdsMetrics = filterMetrics(search, preFilteredMetrics);
    const filteredGoogleAnalyticsMetrics = filterMetrics(
      search,
      googleAnalyticsData.metrics.filter(
        (metric) =>
          metric.label !== 'Event name' && metric.key !== 'event_name', // eventuale chiave tecnica
      ),
    );
    const filteredGoogleSearchConsoleMetrics = filterMetrics(
      search,
      googleSearchConsoleData?.metrics || [],
    );
    const filteredShopifyMetrics = filterMetrics(
      search,
      shopifyData.metrics.filter((metric) => metric.label !== 'Customers'),
    );
    const filteredMagentoMetrics = filterMetrics(
      search,
      magentoData?.metrics || [],
    );
    const filteredKipifyMetrics = filterMetrics(
      search,
      kipifyData?.metrics || [],
    );
    const filteredCustomFormulaMetrics = filterMetrics(
      search,
      customFormulaData.metrics,
    );
    const filteredDatabaseMetrics = filterMetrics(
      search,
      databaseMetrics || [],
    );

    const metricsCount = [
      ...filteredFacebookMetrics,
      ...filteredGoogleAdsMetrics,
      ...filteredGoogleAnalyticsMetrics,
      ...filteredGoogleSearchConsoleMetrics,
      ...filteredShopifyMetrics,
      ...filteredMagentoMetrics,
      ...filteredKipifyMetrics,
      ...filteredCustomFormulaMetrics,
      ...filteredDatabaseMetrics,
    ].length;

    return {
      facebookMetrics: groupMetricsByCategory(filteredFacebookMetrics),
      googleAdsMetrics: groupMetricsByCategory(filteredGoogleAdsMetrics),
      googleAnalyticsMetrics: groupMetricsByCategory(
        filteredGoogleAnalyticsMetrics,
      ),
      googleSearchConsoleMetrics: groupMetricsByCategory(
        filteredGoogleSearchConsoleMetrics,
      ),
      shopifyMetrics: groupMetricsByCategory(filteredShopifyMetrics),
      magentoMetrics: groupMetricsByCategory(filteredMagentoMetrics),
      kipifyMetrics: groupMetricsByCategory(filteredKipifyMetrics),
      customFormulaMetrics: groupMetricsByCategory(
        filteredCustomFormulaMetrics,
      ),
      databaseMetrics: groupMetricsByCategory(filteredDatabaseMetrics),
      metricsCount,
      hasDatabaseMetrics:
        !!databaseConnectorId && filteredDatabaseMetrics.length > 0,
    };
  }, [
    search,
    facebookData.metrics,
    preFilteredMetrics,
    googleAnalyticsData.metrics,
    googleSearchConsoleData?.metrics,
    shopifyData.metrics,
    magentoData?.metrics,
    kipifyData?.metrics,
    customFormulaData.metrics,
    databaseMetrics,
    databaseConnectorId,
  ]);

  const renderMetricsDropdown = (
    type: ConnectorType | MetricsByConnectorType,
    label: string,
    icon: JSX.Element,
    metrics: Record<string, Metric[]>,
    loading: boolean,
    showConfigureButton?: boolean,
  ) =>
    Object.values(metrics).length ? (
      <MetricsDropdown
        type={type}
        label={label}
        icon={icon}
        expanded={!!expandedCollapseType?.includes(type)}
        metricsByCategory={metrics}
        loading={loading}
        onClickMetric={handleClickMetric}
        onToggle={(type: ConnectorType | MetricsByConnectorType) =>
          onToggleCollapse(type, !!search)
        }
        showConfigureButton={showConfigureButton}
      />
    ) : null;

  return (
    <MetricsCard
      ref={metricsContainerRef}
      sx={{
        width: isSmallScreen ? '100%' : '300px',
        padding: isSmallScreen ? '20px' : undefined,
      }}
    >
      <MetricsCardTitle>Metrics</MetricsCardTitle>
      <SearchInput
        placeholder="Find a metric"
        name="search"
        value={search}
        onDelete={clearSearch}
        onChange={handleChangeSearch}
      />
      <CollapsesContainer>
        {memoizedMetrics.metricsCount || memoizedMetrics.hasDatabaseMetrics ? (
          <>
            {memoizedMetrics.hasDatabaseMetrics ? (
              <MetricsDropdown
                type={ConnectorType.DATABASE}
                label="Database"
                icon={
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#1976d2',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    DB
                  </div>
                }
                expanded={
                  !!expandedCollapseType?.includes(ConnectorType.DATABASE)
                }
                metricsByCategory={memoizedMetrics.databaseMetrics}
                loading={false}
                onClickMetric={handleClickMetric}
                onToggle={(type: ConnectorType | MetricsByConnectorType) =>
                  onToggleCollapse(type, !!search)
                }
              />
            ) : null}
            {kipifyData?.hasConnector &&
              renderMetricsDropdown(
                MetricsByConnectorType.KIPIFY,
                ConnectorLabel.KIPIFY,
                <img
                  src={kipifyLogo}
                  alt="Kipify"
                  style={{
                    width: '20px',
                    height: '20px',
                    objectFit: 'contain',
                  }}
                />,
                memoizedMetrics.kipifyMetrics,
                kipifyData.loading,
              )}
            {customFormulaData.hasConnector &&
              renderMetricsDropdown(
                MetricsByConnectorType.CUSTOM_FORMULA,
                ConnectorLabel.CUSTOM_FORMULA,
                <CustomFormulaIcon />,
                memoizedMetrics.customFormulaMetrics,
                customFormulaData.loading,
              )}
            {shopifyData.hasConnector &&
              renderMetricsDropdown(
                ConnectorType.SHOPIFY,
                ConnectorLabel.SHOPIFY,
                <ShopifyIcon />,
                memoizedMetrics.shopifyMetrics,
                shopifyData.loading,
              )}
            {magentoData?.hasConnector &&
              renderMetricsDropdown(
                ConnectorType.MAGENTO,
                ConnectorLabel.MAGENTO,
                <MagentoIcon width={20} height={20} />,
                memoizedMetrics.magentoMetrics,
                magentoData.loading,
              )}
            {googleAnalyticsData.hasConnector &&
              renderMetricsDropdown(
                ConnectorType.GOOGlE_ANALYTICS4,
                ConnectorLabel.GOOGlE_ANALYTICS4,
                <GoogleAnalyticsIcon />,
                memoizedMetrics.googleAnalyticsMetrics,
                googleAnalyticsData.loading,
                !googleAnalyticsData.hasChoosenConnector,
              )}
            {googleSearchConsoleData?.hasConnector &&
              renderMetricsDropdown(
                ConnectorType.GOOGLE_SEARCH_CONSOLE,
                ConnectorLabel.GOOGLE_SEARCH_CONSOLE,
                <GoogleSearchConsoleIcon />,
                memoizedMetrics.googleSearchConsoleMetrics,
                !!googleSearchConsoleData.loading,
                !googleSearchConsoleData.hasChoosenConnector,
              )}
            {googleAdsData.hasConnector &&
              renderMetricsDropdown(
                ConnectorType.GOOGLE_ADS,
                ConnectorLabel.GOOGLE_ADS,
                <GoogleAdsIcon />,
                memoizedMetrics.googleAdsMetrics,
                googleAdsData.loading,
                !googleAdsData.hasChoosenConnector,
              )}
            {facebookData.hasConnector &&
              renderMetricsDropdown(
                ConnectorType.FACEBOOK,
                'Facebook Ads',
                <FacebookAdsIcon />,
                memoizedMetrics.facebookMetrics,
                facebookData.loading,
              )}
          </>
        ) : (
          <Typography m="auto">No metrics</Typography>
        )}
      </CollapsesContainer>
    </MetricsCard>
  );
};
